// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, DatePicker, Form, Row, Select, Switch } from "antd";
import { NewsFormSectionProps } from "../../../types/NewsTypes";
import FormField from "../../../components/FormField";
import {
  BroadcastListInfoResponseDTO,
  BroadcastListsSearchRequestDTO,
  KeywordInfoResponseDTO,
  NewsThemeInfoResponseDTO
} from "../../../../api/backend-openapi";
import {
  dateTimeFormat,
  FIELD_LENGTH,
  LAZY_SELECT_PAGE_SIZE,
  NEWS_PRIORITY,
  orderDescend
} from "../../../../utils/Constant";
import NewsPriorityTag from "./NewsPriorityTag";
import locale from "antd/es/date-picker/locale/fr_FR";
import { useStore } from "effector-react";
import { NewsThemeStore } from "../../../../state/newsTheme/NewsThemeStore";
import { KeywordStore } from "../../../../state/keyword/KeywordStore";
import { requestGetNewsThemes } from "../../../../state/newsTheme/NewsThemeEffects";
import { requestGetKeywords } from "../../../../state/keyword/KeywordEffects";
import { setNewsThemes } from "../../../../state/newsTheme/NewsThemeEvents";
import { setKeywords } from "../../../../state/keyword/KeywordEvents";
import DateFormat from "../../../../utils/DateFormat";
import { BranchesStore } from "../../../../state/branches/BranchesStore";
import { displayError } from "../../../../utils/displayError";
import i18n from "i18next";
import { showInfo } from "../../../components/InfoModal";
import moment from "moment";
import {
  NEWS_AUDIENCES,
  NewsAudienceEnum
} from "../../../../utils/enum/news.audience";
import { LazySelectFormField } from "../../../components/LazySelectFormField";
import { requestSearchBroadcastLists } from "../../../../state/broadcastLists/BroadcastListsEffects";
import { TablePaginationConfig } from "antd/lib/table/interface";
import { preferedRowsPerPageChanged } from "../../../../state/localPreferences/localPreferences";

type NewsSettingsFormSectionProps = NewsFormSectionProps & {
  resetNewsTheme: () => void;
  branch: string | undefined;
  setBranch: React.Dispatch<React.SetStateAction<string | undefined>>;
  sendPushValueToDisplay: boolean;
  setSendPushValueToDisplay: React.Dispatch<React.SetStateAction<boolean>>;
  hideFromHomepageValueToDisplay: boolean;
  setHideFromHomepageValueToDisplay: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  canSendPush: boolean;
  setCanSendPush: React.Dispatch<React.SetStateAction<boolean>>;
  priorityOkForPush: boolean;
  setPriorityOkForPush: React.Dispatch<React.SetStateAction<boolean>>;
  scheduledAvailabilityOkForPush: boolean;
  setScheduledAvailabilityOkForPush: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

const NewsSettingsFormSection: React.FunctionComponent<NewsSettingsFormSectionProps> = (
  props: NewsSettingsFormSectionProps
) => {
  const { t } = useTranslation();

  const {
    news,
    editMode,
    previewFields,
    setPreviewFields,
    showRequired,
    resetNewsTheme,
    branch,
    setBranch,
    sendPushValueToDisplay,
    setSendPushValueToDisplay,
    hideFromHomepageValueToDisplay,
    setHideFromHomepageValueToDisplay,
    setCanSendPush,
    priorityOkForPush,
    setPriorityOkForPush,
    scheduledAvailabilityOkForPush,
    setScheduledAvailabilityOkForPush
  } = props;

  const latestPushDate = news?.latestPushDate;

  const [scheduledAvailabilityDate, setScheduledAvailabilityDate] = useState(
    news?.scheduledAvailabilityDate
  );

  const [isBroadcastListRequired, setIsBroadcastListRequired] = useState(
    news?.audience === NewsAudienceEnum.BROADCAST_LIST
  );

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: LAZY_SELECT_PAGE_SIZE,
    showSizeChanger: true
  });
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [paginationOver, setPaginationOver] = useState<boolean>(false);
  const [broadcastListsLoading, setBroadcastListsLoading] = useState<boolean>(
    false
  );
  const [paginatedBroadcastLists, setPaginatedBroadcastLists] = useState<
    BroadcastListInfoResponseDTO[]
  >([]);

  const newsThemeStore: NewsThemeInfoResponseDTO[] | null = useStore(
    NewsThemeStore
  );
  const branchList = useStore(BranchesStore);

  const keywordsStore: KeywordInfoResponseDTO[] | null = useStore(KeywordStore);

  useEffect(() => {
    requestGetNewsThemes();
  }, []);

  useEffect(() => {
    requestGetKeywords();
  }, []);

  useEffect(() => {
    return requestGetNewsThemes.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setNewsThemes(result.data);
      } else {
        displayError("news_themes.load_failed");
      }
    });
  });

  useEffect(() => {
    return requestGetKeywords.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setKeywords(result.data);
      } else {
        displayError("keywords.load_failed");
      }
    });
  });

  useEffect(() => {
    setBroadcastListsLoading(true);
    const searchCriteria: BroadcastListsSearchRequestDTO = {
      name: query
    };
    requestSearchBroadcastLists({
      pagination,
      sorter: {
        columnKey: "creationDate",
        order: orderDescend
      },
      dto: searchCriteria
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, pagination.current]);

  useEffect(() => {
    return requestSearchBroadcastLists.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setPaginatedBroadcastLists(
          paginatedBroadcastLists?.concat(result.data.listBroadcastListDTO) ??
            result.data.listBroadcastListDTO
        );

        setBroadcastListsLoading(false);
        setPagination({
          ...pagination,
          total: result.data.count
        });
        setPaginationOver(paginatedBroadcastLists.length === result.data.count);
        preferedRowsPerPageChanged(pagination.pageSize);
      } else {
        displayError("news.load_failed");
      }
    });
  });

  const changeForSendPush = (label: string, value: string) => {
    if (label === "priority") {
      const toReturn = true; //parseInt(value) === 3;
      setCanSendPush(toReturn && scheduledAvailabilityOkForPush);
      setPriorityOkForPush(toReturn);
    } else {
      const toReturn = value === "";
      setCanSendPush(toReturn && priorityOkForPush);
      setScheduledAvailabilityOkForPush(toReturn);
    }
  };

  const showWarning = (value: string) => {
    if (value === "4") {
      showInfo(i18n.t("news.forcePush"));
    }
  };
  return (
    <>
      <Row>
        <Col xs={24}>
          <FormField
            textArea={true}
            labelName={t("news.title")}
            inputId="title"
            required={showRequired}
            emptyErrorMessage={t("global.empty_error_field")}
            readOnly={!editMode}
            onChange={e =>
              setPreviewFields({
                ...previewFields,
                title: e.target.value
              })
            }
            maxLength={FIELD_LENGTH.TITLE}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={12}>
          <Form.Item
            name="branch"
            key="branch"
            rules={
              showRequired
                ? [
                    {
                      required: true,
                      message: t("global.empty_error_field"),
                      whitespace: true
                    }
                  ]
                : []
            }
            label={<span>{t("add_news.branch.select")}</span>}
          >
            <Select
              id="branch"
              placeholder={t("add_news.branch.placeholder")}
              disabled={!editMode}
              onChange={(e: string) => {
                setBranch(e);
                resetNewsTheme();
              }}
            >
              {Object.keys(branchList).map(key => (
                <Select.Option key={key} value={key}>
                  {branchList[key].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="newsThemeId"
            key="newsThemeId"
            rules={
              showRequired
                ? [
                    {
                      required: true,
                      message: t("global.empty_error_field"),
                      whitespace: true
                    }
                  ]
                : []
            }
            label={<span>{t("add_news.theme.select")}</span>}
          >
            <Select
              id="newsThemeId"
              placeholder={
                !!branch
                  ? t("add_news.theme.placeholder")
                  : t("add_news.theme.placeholderNoBranch")
              }
              disabled={!editMode}
              onChange={(e: string) =>
                setPreviewFields({
                  ...previewFields,
                  newsThemeId: e
                })
              }
            >
              {newsThemeStore
                .filter(
                  (newsTheme: NewsThemeInfoResponseDTO) =>
                    newsTheme.branch === branch
                )
                .map((newsTheme: NewsThemeInfoResponseDTO) => (
                  <Select.Option key={newsTheme.id} value={newsTheme.id}>
                    {newsTheme.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={12}>
          <Form.Item
            name="audience"
            key="audience"
            rules={
              showRequired
                ? [
                    {
                      required: true,
                      message: t("global.empty_error_field"),
                      whitespace: true
                    }
                  ]
                : []
            }
            label={<span>{t("add_news.audience.select")}</span>}
          >
            <Select
              id="audience"
              placeholder={t("add_news.audience.placeholder")}
              disabled={!editMode}
              onChange={values =>
                setIsBroadcastListRequired(
                  values === NewsAudienceEnum.BROADCAST_LIST
                )
              }
            >
              {NEWS_AUDIENCES.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {t(option.label)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="broadcastListId"
            key="broadcastListId"
            rules={
              isBroadcastListRequired
                ? [
                    {
                      required: true,
                      message: t("global.empty_error_field"),
                      whitespace: true
                    }
                  ]
                : []
            }
            label={<span>{t("add_news.broadcast_list.select")}</span>}
          >
            <LazySelectFormField
              id="broadcastListId"
              placeholder={t("add_news.broadcast_list.placeholder")}
              disabled={!editMode || !isBroadcastListRequired}
              onChange={values =>
                setIsBroadcastListRequired(
                  values === NewsAudienceEnum.BROADCAST_LIST
                )
              }
              showSearch
              onSearch={(input: string) => {
                setQuery(input);
                setPaginatedBroadcastLists([]);
                setPagination({ ...pagination, current: 1 });
              }}
              onLoadMore={() => {
                setPagination({
                  ...pagination,
                  current: (pagination?.current ?? 1) + 1
                });
              }}
              isPaginationOver={paginationOver}
              loading={broadcastListsLoading}
              options={
                paginatedBroadcastLists
                  ? paginatedBroadcastLists.map(code => {
                      return { value: code.id, label: code.name };
                    })
                  : []
              }
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="priority"
            key="priority"
            label={<span>{t("add_news.priority.select")}</span>}
            rules={
              showRequired
                ? [
                    {
                      required: true,
                      message: t("global.empty_error_field"),
                      whitespace: true
                    }
                  ]
                : []
            }
          >
            {editMode ? (
              <Select
                id="priority"
                placeholder={t("add_news.priority.placeholder")}
                disabled={!editMode}
                onChange={(value: string) => {
                  changeForSendPush("priority", value);
                  showWarning(value);
                }}
              >
                {NEWS_PRIORITY.filter(
                  priority =>
                    priority.value !== "4" ||
                    (branch && !branch.startsWith("crcc"))
                ).map((priority: any) => (
                  <Select.Option key={priority.value} value={priority.value}>
                    {t(`add_news.priority.${priority.label}`)}
                  </Select.Option>
                ))}
              </Select>
            ) : news?.priority ? (
              <NewsPriorityTag priority={news?.priority} />
            ) : (
              ""
            )}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="scheduledAvailabilityDate"
            key="scheduledAvailabilityDate"
            label={<span>{t("news.scheduled_availability_date")}</span>}
          >
            <DatePicker
              disabled={!editMode}
              locale={locale}
              id="scheduledAvailabilityDate"
              showTime
              format={dateTimeFormat}
              onChange={(value: moment.Moment | null, dateString: string) => {
                setScheduledAvailabilityDate(
                  value ? value.toDate() : undefined
                );
                changeForSendPush("availabilityDate", dateString);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={8}>
          <Form.Item
            name="scheduledUnpublishDate"
            key="scheduledUnpublishDate"
            label={<span>{t("news.scheduled_unpublish_date")}</span>}
          >
            <DatePicker
              disabled={!editMode}
              locale={locale}
              id="scheduledUnpublishDate"
              showTime
              disabledDate={d =>
                !d ||
                (!!scheduledAvailabilityDate &&
                  d.isBefore(scheduledAvailabilityDate, "minute"))
              }
              format={dateTimeFormat}
            />
          </Form.Item>
        </Col>
      </Row>
      {/* TO DO LA VALUE DE NEWS?.NOTIFY QUI REVIENT DU SERVEUR*/}
      <Row>
        {!!latestPushDate && (
          <Col xs={24} sm={12}>
            <FormField
              labelName={t("news.latest_push_date")}
              defaultValue={DateFormat.standardDate(latestPushDate)}
              inputId="latestPushDate"
              readOnly={true}
            />
          </Col>
        )}
        {
          <Col xs={24} sm={!!latestPushDate ? 12 : 24}>
            <Form.Item
              name="sendPush"
              key="sendPush"
              label={<span>{t("news.sendPush")}</span>}
            >
              <Switch
                checked={sendPushValueToDisplay}
                onChange={(e: boolean) => {
                  setSendPushValueToDisplay(e);
                }}
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
        }
      </Row>
      <Row>
        {
          <Col xs={24}>
            <Form.Item
              name="hideFromHomepage"
              key="hideFromHomepage"
              label={<span>{t("news.hideFromHomepage")}</span>}
            >
              <Switch
                checked={hideFromHomepageValueToDisplay}
                onChange={(e: boolean) => {
                  setHideFromHomepageValueToDisplay(e);
                }}
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
        }
      </Row>
      <Row>
        <Col xs={24}>
          <Form.Item
            name="eventDates"
            key="eventDates"
            label={<span>{t("news.event_dates")}</span>}
          >
            <DatePicker.RangePicker
              disabled={!editMode}
              locale={locale}
              id="eventDates"
              showTime={{
                defaultValue: [
                  moment("09:00:00", "HH:mm:ss"),
                  moment("18:00:00", "HH:mm:ss")
                ]
              }}
              format={dateTimeFormat}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          <Form.Item
            name="keywords"
            key="keywords"
            label={<span>{t("news.keywords.select")}</span>}
          >
            <Select
              id="keywords"
              placeholder={t("news.keywords.placeholder")}
              disabled={!editMode}
              mode="multiple"
              className="wdio-keywords"
              optionFilterProp="label"
              options={keywordsStore.map((keyword: KeywordInfoResponseDTO) => ({
                label: keyword.text,
                value: keyword.id
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      {news && (
        <Row>
          <Col xs={24} md={12}>
            <FormField labelName="Id" inputId="id" readOnly={true} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <FormField
              labelName={t("news.creation_date")}
              inputId="creationDate"
              readOnly={true}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <FormField
              labelName={t("news.modification_date")}
              inputId="modificationDate"
              readOnly={true}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export default NewsSettingsFormSection;
